import {
  useCreateOwnAppraisalMutation,
  useDraftOwnAppraisalMutation,
  useGetOwnAppraisalDetailsQuery,
} from "@/app/features/employee/performance/performance.api";
import CustomDrawer from "@/components/CustomDrawer";
import MultiFileUploader from "@/components/MultiFileUploader";
import WithLoader from "@/components/WithLoader";
import useAuth from "@/hooks/useAuth";
import { cn } from "@/lib/cn";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { getFileNameFromUrl } from "@/utils";
import { Button, Flex, Form, Input, InputNumber } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";

// ──────── Types ────────
interface ManageOwnAppraisalProps {
  open: boolean;
  onClose: () => void;
  planName?: string;
  mappingId?: string;
  planId?: string;
}

type FormType = {
  appraisals: {
    kpi_id: string;
    kpi_name: string;
    weightage: number;
    self_evaluation: string;
    self_rating: number;
  }[];
  employee_comment?: string;
  attachments?: string[];
};

// ──────── Component ────────
const ManageOwnAppraisal = ({
  open,
  onClose,
  planName,
  mappingId,
  planId,
}: ManageOwnAppraisalProps) => {
  const { userId, organizationId } = useAuth();
  const [form] = Form.useForm<FormType>();

  // Mutations
  const [draftAppraisal, { isLoading: isDrafting }] =
    useDraftOwnAppraisalMutation();
  const [submitAppraisal, { isLoading: isSubmitting }] =
    useCreateOwnAppraisalMutation();

  // Fetch Appraisal Details to populate the form
  const { data: detailRes, isFetching: isDetailFetching } =
    useGetOwnAppraisalDetailsQuery(mappingId || "", {
      skip: !mappingId || !open,
    });

  const employeeKpis = detailRes?.data?.employeeKpis || [];

  useEffect(() => {
    if (detailRes?.data) {
      const targetData = detailRes.data;
      const kpis = targetData.employeeKpis || [];
      const attachments = targetData.attachments || [];

      form.setFieldsValue({
        employee_comment: targetData.employee_comment || "",
        appraisals: kpis.map((k: any) => ({
          kpi_id: k.id,
          kpi_name: k.kpiName,
          weightage: Number(k.weightage),
          self_evaluation: k.selfEvaluation || "",
          self_rating: k.selfRating || null,
        })),
        attachments: attachments?.map((file: any) => file.fileUrl),
      });
    }
  }, [detailRes, form, open]);

  const handleSubmit = (type: "draft" | "submit") => {
    form
      .validateFields()
      .then(async (values) => {
        const payload = {
          mapping_id: mappingId || "",
          plan_id: planId || "",
          organization_id: organizationId || "",
          employee_id: userId || "",
          employee_comment: values.employee_comment || "",
          appraisals: values.appraisals.map((a) => ({
            kpi_id: a.kpi_id,
            self_rating: Number(a.self_rating),
            self_evaluation: a.self_evaluation,
          })),
          attachments:
            values.attachments?.map((url) => ({
              file_name: getFileNameFromUrl(url),
              file_url: url,
            })) || [],
        };

        console.log(`Final ${type} payload:`, payload);

        try {
          let res;
          if (type === "draft") {
            res = await draftAppraisal(payload).unwrap();
          } else {
            res = await submitAppraisal(payload).unwrap();
          }

          if (res?.success === false) {
            toast("error", getErrorMessage(res, `Failed to ${type} Appraisal`));
            return;
          }

          toast(
            "success",
            `Appraisal ${
              type === "draft" ? "drafted" : "submitted"
            } successfully`,
          );
          handleDrawerClose();
        } catch (error: any) {
          toast("error", getErrorMessage(error, `Failed to ${type} Appraisal`));
        }
      })
      .catch((err) => {
        console.error("Validation Failed:", err);
      });
  };

  const handleDrawerClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleDrawerClose}
      title={planName ? `${planName} - Appraisal` : "Manage Appraisal"}
    >
      <WithLoader
        isLoading={isDetailFetching}
        className={cn(
          "rounded-lg",
          isDetailFetching && "h-[calc(100vh_-_105px)]",
        )}
      >
        <Form
          form={form}
          layout="vertical"
          className="custom-form"
          size="large"
        >
          <>
            <div className="border border-theme-gray-700 rounded-xl p-6">
              <h4 className="text-white text-base font-semibold mb-6">
                KPI Performance Appraisal{" "}
                <span className="text-red-500">*</span>
              </h4>

              <Form.List name="appraisals">
                {(fields) => (
                  <>
                    {fields.map((field, index) => {
                      const { key, ...restField } = field;
                      return (
                        <div key={key} className="relative">
                          {index > 0 && (
                            <div className="border-t border-dashed border-theme-gray-600 my-6" />
                          )}

                          <Flex
                            justify="space-between"
                            align="center"
                            className="mb-4"
                          >
                            <h5 className="text-white text-sm font-medium ">
                              KPI -{index + 1}
                            </h5>
                          </Flex>

                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="KPI Name"
                              name={[field.name, "kpi_name"]}
                              className="mb-4"
                            >
                              <Input disabled className="bg-slate-800/50" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Weightage(%)"
                              name={[field.name, "weightage"]}
                              className="mb-4"
                            >
                              <InputNumber
                                disabled
                                controls={false}
                                className="w-full bg-slate-800/50"
                              />
                            </Form.Item>
                          </div>

                          {employeeKpis[index]?.details && (
                            <div className="mt-4">
                              <label className="text-white text-sm block mb-2">
                                Details
                              </label>
                              <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[60px] whitespace-pre-wrap">
                                {employeeKpis[index].details}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="Comment"
                              name={[field.name, "self_evaluation"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Comment is required",
                                },
                              ]}
                              className="mb-4"
                            >
                              <TextArea
                                rows={4}
                                placeholder="Enter your evaluation"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              initialValue={1}
                              label="Rating Scale (1-5)"
                              name={[field.name, "self_rating"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Rating is required",
                                },
                                {
                                  type: "number",
                                  min: 1,
                                  max: 5,
                                  message: "Rating must be between 1 and 5",
                                },
                              ]}
                              className="mb-4"
                            >
                              <InputNumber
                                min={1}
                                max={10}
                                placeholder="0"
                                className="w-full"
                              />
                            </Form.Item>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </Form.List>
              {/* ── Overall Comment ── */}
              <Form.Item
                label="Overall Comment"
                name="employee_comment"
                className="mb-0 !mt-2"
              >
                <TextArea
                  rows={4}
                  placeholder="Enter overall comment"
                />
              </Form.Item>
            </div>

            <Form.Item className="mt-8" name="attachments">
              <MultiFileUploader
                form={form}
                name="attachments"
                title="Drag & Drop your file here"
                description="Browse"
              />
            </Form.Item>

            <Flex justify="end" gap={12} className="mt-12">
              <Button
                size="large"
                className="btn-primary-bordered min-w-24"
                onClick={() => handleSubmit("draft")}
                loading={isDrafting}
              >
                Draft
              </Button>
              <Button
                size="large"
                className="btn-primary min-w-24"
                onClick={() => handleSubmit("submit")}
                loading={isSubmitting}
              >
                Submit
              </Button>
            </Flex>
          </>
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
};

export default ManageOwnAppraisal;
